<template>
	<div class="calendar-month">
		<div class="calendar-header">
			<button v-if="showLeftNav" type="button" class="nav-button" @click="$emit('navigate', -1)" aria-label="Previous month" :disabled="isNavDisabled">
				<VIcon>mdi-chevron-left</VIcon>
			</button>

			<div class="header-selectors">
				<div class="selector-button month-button">
					<span>{{ translatedMonthNames[month] ?? '' }}</span>
					<VIcon size="16">mdi-menu-down</VIcon>
					<select :value="month" @change="handleMonthChange" class="hidden-select" :disabled="isDropdownDisabled">
						<option v-for="(monthName, index) in translatedMonthNames" :key="index" :value="index">
							{{ monthName }}
						</option>
					</select>
				</div>

				<div class="selector-button year-button">
					<span>{{ year }}</span>
					<VIcon size="16">mdi-menu-down</VIcon>
					<select :value="year" @change="handleYearChange" class="hidden-select" :disabled="isDropdownDisabled">
						<option v-for="y in availableYears" :key="y" :value="y">
							{{ y }}
						</option>
					</select>
				</div>
			</div>

			<button v-if="showRightNav" type="button" class="nav-button" @click="$emit('navigate', 1)" aria-label="Next month" :disabled="isNavDisabled">
				<VIcon>mdi-chevron-right</VIcon>
			</button>
		</div>

		<div class="calendar-grid">
			<div v-for="day in weekDays" :key="day" class="weekday">
				{{ day }}
			</div>

			<div v-for="(day, index) in calendarDays" :key="index" class="day-cell-container">
				<button
					class="day-cell"
					:class="{
						start: day.isStart,
						end: day.isEnd,
						'in-range': day.isInRange,
						'range-complete': day.isRangeComplete,
						'other-month': !day.isCurrentMonth,
					}"
					:disabled="!day.isCurrentMonth || isNavDisabled"
					@click="handleDayClick(day)"
				>
					<span class="day-text">{{ day.date.getDate() }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getDaysInMonth, isSameDay, isDateInRange } from '../utils/date-utils';
import type { CalendarDay } from '../utils/date-utils';

interface Props {
	year: number;
	month: number;
	startDate: Date | null;
	endDate: Date | null;
	isPreset?: boolean;
	showLeftNav?: boolean;
	showRightNav?: boolean;
	disableDropdowns?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isPreset: false,
	showLeftNav: false,
	showRightNav: false,
	disableDropdowns: false,
});

const emit = defineEmits<{
	(event: 'update:year', value: number): void;
	(event: 'update:month', value: number): void;
	(event: 'dateClick', date: Date): void;
	(event: 'navigate', direction: -1 | 1): void;
}>();

const { t } = useI18n();

const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

const translatedMonthNames = computed(() => monthKeys.map((key) => t(`months.${key}`)));

const availableYears = computed(() => {
	const currentYear = new Date().getFullYear();
	return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
});

const isNavDisabled = computed(() => props.isPreset);
const isDropdownDisabled = computed(() => props.isPreset || props.disableDropdowns);
const showDropdownIcon = computed(() => !props.isPreset && !props.disableDropdowns);

const calendarDays = computed(() => {
	const days: CalendarDay[] = getDaysInMonth(props.year, props.month);
	const isRangeComplete = !!(props.startDate && props.endDate);

	return days.map(({ date, isCurrentMonth }) => {
		const isStart = isCurrentMonth && isSameDay(date, props.startDate);
		const isEnd = isCurrentMonth && isSameDay(date, props.endDate);
		const isSelected = isStart || isEnd;
		const isInRange = isCurrentMonth && isDateInRange(date, props.startDate, props.endDate) && !isSelected;

		return {
			date,
			isCurrentMonth,
			isStart,
			isEnd,
			isInRange,
			isRangeComplete,
		};
	});
});

const handleMonthChange = (event: Event) => {
	if (!(event.target instanceof HTMLSelectElement)) {
		return;
	}

	const value = parseInt(event.target.value, 10);
	if (isNaN(value) || value < 0 || value > 11) {
		return;
	}

	emit('update:month', value);
};

const handleYearChange = (event: Event) => {
	const target = event.target as HTMLSelectElement;
	emit('update:year', parseInt(target.value, 10));
};

const handleDayClick = (day: { date: Date; isCurrentMonth: boolean }) => {
	if (!day.isCurrentMonth) {
		return;
	}
	emit('dateClick', day.date);
};
</script>

<style scoped>
.calendar-month {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 0;
	gap: 10px;
	background: transparent;
	box-sizing: border-box;
	opacity: 1;
}

.calendar-header {
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 252px;
	min-height: 36px;
	box-sizing: border-box;
}

.nav-button {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	width: 20px;
	height: 28px;
	border-radius: 32px;
	background: none;
	border: none;
	cursor: pointer;
	box-sizing: border-box;
	opacity: 1;
}

.nav-button .v-icon {
	width: 20px;
	height: 20px;
	font-family: 'Material Icons';
	font-weight: 400;
	font-size: 20px;
	line-height: 100%;
	letter-spacing: 0%;
	text-align: center;
	vertical-align: middle;
	color: rgba(0, 0, 0, 0.87);
}

.header-selectors {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 8px;
	flex: 1;
}

.selector-button {
	box-sizing: border-box;
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 4px 8px;
	gap: 12px;
	height: 28px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	position: relative;
	cursor: pointer;
	opacity: 1;
}

.month-button {
	flex: 1 1 auto;
}
.year-button {
	flex: 0 0 auto;
}

.selector-button span {
	font-family: 'Roboto';
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	letter-spacing: -0.15px;
	color: #424242;
	flex: none;
	order: 0;
	flex-grow: 1;
}

.selector-button .v-icon {
	flex: 0 0 auto;
	width: 16px;
	height: 16px;
	display: inline-flex !important;
	align-items: center;
	justify-content: center;
	font-family: 'Material Icons' !important;
	font-weight: 400;
	font-size: 16px;
	line-height: 1;
	color: #9e9e9e;
	visibility: visible !important;
	opacity: 1 !important;
}

.hidden-select {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	cursor: pointer;
}

.calendar-grid {
	width: 252px;
	height: 218px;
	display: grid;
	grid-template-columns: repeat(7, 36px);
	grid-template-rows: 20px repeat(6, 33px);
	opacity: 1;
	background: #ffffff;
}

.weekday {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	height: 20px;
	font-family: 'Roboto';
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	color: rgba(0, 0, 0, 0.38);
}

.day-cell-container {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 0px;
	width: 36px;
	height: 33px;
}

.day-cell {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 0px 16px;
	width: 32px;
	height: 32px;
	border-radius: 32px;
	border: none;
	background: none;
	cursor: pointer;
	position: relative;
}

.day-text {
	width: auto;
	height: 20px;
	font-family: 'Roboto';
	font-style: normal;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	display: flex;
	align-items: center;
	text-align: center;
	color: rgba(0, 0, 0, 0.87);
}

.day-cell.other-month {
	visibility: hidden;
	pointer-events: none;
}

.day-cell.start,
.day-cell.end {
	background: #10565c;
}

.day-cell.start.range-complete {
	border-radius: 32px 0 0 32px;
}
.day-cell.end.range-complete {
	border-radius: 0 32px 32px 0;
}

.day-cell.start .day-text,
.day-cell.end .day-text {
	color: #ffffff;
}

.day-cell.in-range {
	background: #eaf1f1;
	border-radius: 0;
	width: 36px;
	height: 34px;
}

.day-cell:disabled {
	cursor: default;
}

.nav-button:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}
</style>
